
/**
 * Module dependencies.
 */

import { Icon } from 'react-components/media';
import { IconButton, ImageSources, LocalImageSources, useImageSources } from 'slyk-web-libs/design-system';
import { color, media } from 'react-components/styles';
import { getImageSources } from 'client/core/utils/image';
import { ifProp, theme } from 'styled-tools';
import { isNil } from 'lodash';
import { useBreakpoint } from 'slyk-web-libs/breakpoint';
import { useTranslate } from 'slyk-web-libs/i18n';
import DefaultMediaGallery from 'react-image-gallery';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import arrowLeft16px from 'client/assets/svg/16/arrow-left.svg';
import arrowRight16px from 'client/assets/svg/16/arrow-right.svg';
import mediaGalleryStyle from 'react-image-gallery/styles/css/image-gallery.css';
import soundOffIcon from 'client/assets/svg/32/sound-off.svg';
import soundOnIcon from 'client/assets/svg/32/sound-on.svg';
import styled, { css } from 'styled-components';

/**
 * Export `MediaGalleryProps` type.
 */

export type MediaGalleryProps = {
  loading?: 'eager' | 'lazy';
  original: string;
  thumbnail: string;
  thumbnailLoading?: 'eager' | 'lazy';
  type: string;
};

/**
 * `ItemProps` type.
 */

type ItemProps = {
  item: MediaGalleryProps;
 };

/**
 * `Props` type.
 */

type Props = {
  children?: ReactNode;
  className?: string;
  mediaGallery: Array<MediaGalleryProps>;
  onThumbnailError: () => void;
};

/**
 * Image style.
 */

const imageStyle = css`
  aspect-ratio: 3 / 4;
  background-color: ${color('gray200')};
  border-radius: 6px;
  height: auto;
  width: 100%;
`;

/**
 * `MediaGalleryWrapper` styled component.
 */

const MediaGalleryWrapper = styled.div`
  ${mediaGalleryStyle}

  position: relative;

  .image-gallery-slides {
    background-color: ${color('gray100')};
    border: none;
    border-radius: 6px;
    outline: 1px solid ${color('gray100')};
  }

  .image-gallery-thumbnails {
    padding: 16px 0;

    .image-gallery-thumbnails-container {
      text-align: left;
    }
  }

  .image-gallery-thumbnail {
    background-color: ${color('gray100')};
    border: 2px solid ${color('gray300')};
    border-radius: 8px;
    cursor: pointer;
    height: 68px;
    opacity: 0.6;
    transition: ${theme('transitions.default')};
    transition-property: border-color, opacity;
    width: 68px;

    &:first-of-type {
      margin-left: 2px;
    }

    &:last-of-type {
      margin-right: 2px;
    }

    &:hover {
      border: 2px solid ${color('gray300')};
      opacity: 0.9;
    }

    &:focus-visible {
      border: 2px solid ${color('gray500')};
      opacity: 1;
      outline: none;
    }

    &.active {
      border: 2px solid ${color('blue500')};
      opacity: 1;
    }

    .image-gallery-thumbnail-inner {
      border-radius: 6px;
      height: 100%;
      width: 100%;
    }

    .image-gallery-thumbnail-image {
      ${imageStyle}

      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }

  .image-gallery-thumbnail+.image-gallery-thumbnail {
    margin-left: 4px;
  }
`;

/**
 * Nav button style.
 */

const navButtonStyle = css`
  backdrop-filter: blur(4px);
  background-color: ${color.transparentize('white', 0.2)};
  border: 1px solid transparent;
  border-radius: 50%;
  color: ${color('nightBlue900')};
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  transition: border-color ${theme('transitions.default')};
  z-index: 1;

  &:active,
  &:hover {
    border-color: ${color('gray300')};
  }

  &:focus-visible {
    border-color: ${color('gray500')};
  }
`;

/**
 * `RightNavButton` styled component.
 */

const RightNavButton = styled(IconButton)`
  ${navButtonStyle}

  margin-right: 16px;
  right: 0;
`;

/**
 * `LeftNavButton` styled component.
 */

const LeftNavButton = styled(IconButton)`
  ${navButtonStyle}

  left: 0;
  margin-left: 16px;
`;

/**
 * `Video` styled component.
 */

const Video = styled.video`
  ${imageStyle}

  object-fit: cover;
  object-position: center;
`;

/**
 * `Image` styled component.
 */

const Image = styled.img`
  ${imageStyle}

  object-fit: cover;
  object-position: center;
`;

/**
 * `SoundControlButton` styled component.
 */

const SoundControlButton = styled.button<{ isVisible: boolean }>`
  align-items: center;
  appearance: none;
  background: transparent;
  border: none;
  bottom: 0;
  cursor: pointer;
  display: flex;
  justify-content: center;
  left: 0;
  opacity: ${ifProp('isVisible', 1, 0)};
  outline: none;
  position: absolute;
  right: 0;
  top: 0;
  transition: opacity ${theme('animations.defaultTransition')};
  width: 100%;
`;

/**
 * `MaskWrapper` styled component.
 */

const MaskWrapper = styled.div`
  background-color: ${color.transparentize('black', 0.1)};
  border-radius: 68px;
  height: 100px;
  position: absolute;
  width: 100px;

  ${media.min('xs')`
    height: 144px;
    width: 144px;
  `}
`;

/**
 * `IconWrapper` styled component.
 */

const IconWrapper = styled.div`
  align-items: center;
  background-color: ${color.transparentize('black', 0.5)};
  border-radius: 68px;
  display: flex;
  height: 56px;
  justify-content: center;
  width: 56px;

  ${media.min('xs')`
    height: 68px;
    width: 68px;
  `}
`;

/**
 * `StyledIcon` styled component.
 */

const StyledIcon = styled(Icon)`
  position: absolute;
`;

/**
 * Export `Item` component.
 */

function Item({ item }: ItemProps): JSX.Element {
  const { translate } = useTranslate();
  const [isSoundControlVisible, setSoundControlVisible] = useState<boolean>(false);
  const [isMuted, setMuted] = useState<boolean>(true);
  const imageSources = useImageSources(item.original, '128x');
  const [imageSource, setImageSource] = useState<ImageSources | null>(imageSources);
  const noImageSources = getImageSources('no-image/product', 'webp') as LocalImageSources;
  const handleError = useCallback(() => {
    setImageSource(noImageSources);
  }, [noImageSources]);

  if (item.type === 'video') {
    return (
      <div
        onMouseLeave={() => setSoundControlVisible(false)}
        onMouseOver={() => setSoundControlVisible(true)}
      >
        <Video
          autoPlay
          loop
          muted={isMuted}
          playsInline
        >
          <source src={item.original} />
        </Video>

        <SoundControlButton
          aria-label={isMuted ? translate('video.soundOff') : translate('video.soundOn')}
          isVisible={isSoundControlVisible}
          onClick={() => setMuted(!isMuted)}
        >
          <MaskWrapper />

          <IconWrapper>
            <StyledIcon
              aria-hidden
              color={'white'}
              icon={isMuted ? soundOffIcon : soundOnIcon}
              size={'32px'}
            />
          </IconWrapper>
        </SoundControlButton>
      </div>
    );
  }

  return (
    <Image
      alt={''}
      onError={handleError}
      src={imageSource?.defaultUrl ?? noImageSources.defaultUrl}
      srcSet={imageSource?.retinaUrl ?? noImageSources.retinaUrl}
    />
  );
}

/**
 * Export `MediaGallery` component.
 */

export function MediaGallery(props: Props): JSX.Element {
  const { className, mediaGallery, onThumbnailError } = props;
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const { translate } = useTranslate();
  const isMobile = useBreakpoint('sm', 'max');
  const noImageSources = getImageSources('no-image/product', 'webp');
  const formattedGallery = useMemo(() => {
    return mediaGallery?.filter(({ original, thumbnail }) => {
      return !isNil(original) && !isNil(thumbnail);
    }) ?? [];
  }, [mediaGallery]);

  const emptyGallery = useMemo(() => {
    return [{
      original: noImageSources.defaultUrl,
      thumbnail: noImageSources.defaultUrl,
      type: 'image'
    }];
  }, [noImageSources.defaultUrl]);

  return (
    <div className={className}>
      <MediaGalleryWrapper>
        <DefaultMediaGallery
          items={formattedGallery.length === 0 ? emptyGallery : formattedGallery}
          onSlide={(currentIndexSlide: number) => setCurrentIndex(currentIndexSlide)}
          onThumbnailError={onThumbnailError}
          renderItem={(item: MediaGalleryProps) => <Item item={item} />}
          renderLeftNav={(onClick: any, disabled: boolean) => (
            <LeftNavButton
              aria-label={translate('common.actions.previous')}
              colorTheme={'primary'}
              disabled={disabled}
              icon={arrowLeft16px}
              iconSize={'small'}
              onClick={onClick}
              size={'medium'}
            />
          )}
          renderRightNav={(onClick: any, disabled: boolean) => (
            <RightNavButton
              aria-label={translate('common.actions.next')}
              colorTheme={'primary'}
              disabled={disabled}
              icon={arrowRight16px}
              iconSize={'small'}
              onClick={onClick}
              size={'medium'}
            />
          )}
          showFullscreenButton={false}
          showNav
          showPlayButton={false}
          showThumbnails={!isMobile && formattedGallery.length > 1}
          slideOnThumbnailOver={false}
          startIndex={currentIndex}
          swipingTransitionDuration={250}
          useBrowserFullscreen={false}
        />
      </MediaGalleryWrapper>
    </div>
  );
}
